import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Link } from 'react-router-dom'
import {
  Archive, Building2, GraduationCap, Monitor, Shield,
  Sparkles, ArrowRight, Package, Cpu
} from 'lucide-react'

const supplies = [
  { icon: Building2, name: 'Hospital Furniture', desc: 'Medical-grade clinical furniture', color: '#ec4899' },
  { icon: GraduationCap, name: 'Classroom Furniture', desc: 'Smart desks & educational furniture', color: '#10b981' },
  { icon: Archive, name: 'Compactors', desc: 'Mobile storage & archive solutions', color: '#06b6d4' },
  { icon: Monitor, name: 'Interactive Displays', desc: 'Smart boards & digital signage', color: '#f97316' },
  { icon: Shield, name: 'Surveillance Systems', desc: 'CCTV & access control solutions', color: '#ef4444' },
  { icon: Cpu, name: 'IT Infrastructure', desc: 'Servers, networks & lab setup', color: '#6366f1' },
]

export const WhatWeSupply = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <section ref={ref} className="py-20 relative">
      <div className="container-enterprise relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.25)',
              boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
            }}
          >
            <Package className="w-4 h-4 text-primary" />
            <span className="text-sm text-slate-300">Supply Portfolio</span>
          </motion.div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Our <span className="gradient-text">Supply Portfolio</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Premium products and solutions for government and institutional requirements
          </p>
        </motion.div>

        {/* Supplies Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {supplies.map((supply, index) => {
            const Icon = supply.icon
            return (
              <motion.div
                key={supply.name}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group"
              >
                <div className="rounded-2xl p-5 h-full transition-all duration-300 hover:translate-y-[-2px]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `${supply.color}20`,
                        border: `1px solid ${supply.color}40`,
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: supply.color }} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white group-hover:text-primary transition-colors">
                        {supply.name}
                      </h3>
                      <p className="text-sm text-slate-400">{supply.desc}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-10 text-center"
        >
          <Link
            to="/contact"
            className="btn-glow inline-flex items-center gap-2 px-6 py-3 text-white font-semibold rounded-xl"
          >
            <Sparkles className="w-4 h-4" />
            <span>Request Quote</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}